import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ChevronRight, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { getAllProvinces, getDistricts } from '../data/regions';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';

interface TermItem {
  id: string;
  title: string;
  required: boolean;
  content: string;
}

const terms: TermItem[] = [
  {
    id: 'service',
    title: '서비스 이용약관',
    required: true,
    content: `제1조 (목적)
이 약관은 스탬핏(이하 "회사")이 제공하는 피트니스 지도자 자격 인증 및 구인구직 서비스(이하 "서비스")의 이용에 관한 조건 및 절차, 회사와 회원의 권리, 의무 및 책임사항 등을 규정함을 목적으로 합니다.

제2조 (용어의 정의)
1. "서비스"란 회사가 제공하는 피트니스 관련 자격 인증, 교육 정보, 구인구직 등의 온라인 서비스를 말합니다.
2. "회원"이란 이 약관에 동의하고 회사와 서비스 이용계약을 체결한 자를 말합니다.
3. "아이디(ID)"란 회원 식별과 서비스 이용을 위해 회원이 설정하고 회사가 승인한 문자와 숫자의 조합을 말합니다.

제3조 (약관의 효력 및 변경)
1. 이 약관은 서비스를 이용하고자 하는 모든 회원에게 적용됩니다.
2. 회사는 필요한 경우 관련 법령을 위배하지 않는 범위에서 이 약관을 변경할 수 있습니다.

제4조 (서비스의 제공)
회사는 다음과 같은 서비스를 제공합니다:
1. 피트니스 지도자 자격 교육 정보 제공
2. 자격 인증 및 관리 서비스
3. 구인구직 매칭 서비스
4. 기타 회사가 정하는 서비스`,
  },
  {
    id: 'privacy',
    title: '개인정보 처리방침',
    required: true,
    content: `스탬핏(이하 "회사")은 개인정보보호법에 따라 이용자의 개인정보 보호 및 권익을 보호하고 개인정보와 관련한 이용자의 고충을 원활하게 처리할 수 있도록 다음과 같은 처리방침을 두고 있습니다.

1. 개인정보의 수집 및 이용 목적
- 회원 가입 및 관리: 회원제 서비스 이용에 따른 본인확인, 개인식별, 불량회원의 부정이용 방지
- 서비스 제공: 자격 인증, 교육 정보 제공, 구인구직 서비스 등

2. 수집하는 개인정보 항목
- 필수항목: 이메일, 이름, 휴대폰번호
- 선택항목: 프로필 사진, 자격증 정보, 경력 정보

3. 개인정보의 보유 및 이용기간
- 회원 탈퇴 시까지 (단, 관계 법령에 따라 보존이 필요한 경우 해당 기간 동안 보관)

4. 개인정보의 제3자 제공
- 회사는 원칙적으로 이용자의 개인정보를 제3자에게 제공하지 않습니다.
- 다만, 이용자의 동의가 있거나 법령에 의한 경우는 예외로 합니다.

5. 개인정보의 파기
- 회원 탈퇴 시 지체 없이 파기합니다.`,
  },
  {
    id: 'marketing',
    title: '마케팅 정보 수신 동의',
    required: false,
    content: `스탬핏에서 제공하는 이벤트, 프로모션, 신규 서비스 안내 등의 마케팅 정보를 이메일, SMS, 푸시 알림 등으로 수신하는 것에 동의합니다.

- 수신 정보: 신규 교육 과정 안내, 할인 이벤트, 서비스 업데이트 소식
- 수신 방법: 이메일, SMS, 앱 푸시 알림
- 동의 철회: 마이페이지에서 언제든지 수신 거부 가능

※ 마케팅 정보 수신에 동의하지 않아도 서비스 이용에는 제한이 없습니다.`,
  },
];

export default function TermsAgreementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, checkSession } = useAuthStore();

  const [agreements, setAgreements] = useState<Record<string, boolean>>({
    service: false,
    privacy: false,
    marketing: false,
  });
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // URL에서 사용자 정보 가져오기 (OAuth 콜백에서 전달됨)
  const userId = searchParams.get('user_id') || user?.id;
  const provider = searchParams.get('provider') || user?.provider;

  // 소셜 로그인은 provider가 이름/이메일/휴대폰 등을 신뢰성 있게 제공하지 않으므로
  // (카카오는 이메일 동의항목 요청 불가, 구글/카카오 모두 휴대폰 미제공) 최초 로그인 시 직접 입력받는다.
  // 이메일 회원가입(SignupPage)과 동일한 필드를 수집 — 인증서/이력서 등에서 그대로 사용되므로 누락 시 안 됨.
  const [name, setName] = useState(searchParams.get('name') || user?.name || '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState<'' | 'male' | 'female'>('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [phone, setPhone] = useState('');
  const [province, setProvince] = useState('');
  const [district, setDistrict] = useState('');
  const [email, setEmail] = useState(searchParams.get('email') || user?.email || '');

  const handleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProvince(e.target.value);
    setDistrict('');
  };

  const cleanAndConcatenateName = (first: string, last: string): string => {
    const cleanFirst = first.trim().replace(/\s+/g, ' ');
    const cleanLast = last.trim().replace(/\s+/g, ' ');
    if (!cleanLast) return cleanFirst;
    return `${cleanFirst} ${cleanLast}`.trim();
  };

  const allRequiredAgreed = terms
    .filter((t) => t.required)
    .every((t) => agreements[t.id]);

  const allAgreed = terms.every((t) => agreements[t.id]);

  const handleToggle = (id: string) => {
    setAgreements((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleToggleAll = () => {
    const newValue = !allAgreed;
    const newAgreements: Record<string, boolean> = {};
    terms.forEach((t) => {
      newAgreements[t.id] = newValue;
    });
    setAgreements(newAgreements);
  };

  // 빠진 항목/형식 오류를 전부 모아서 반환 — 하나씩 순서대로 걸리면 사용자가 제출을
  // 여러 번 눌러야 전체 목록을 알 수 있어서, 한 번에 모아 모달로 보여주기 위함
  const collectValidationErrors = (): string[] => {
    const errors: string[] = [];
    if (!name.trim()) errors.push('이름 (한글)을 입력해주세요.');
    if (!firstName.trim() || !/^[a-zA-Z\s]+$/.test(firstName)) errors.push('First Name을 영문으로 입력해주세요.');
    if (!lastName.trim() || !/^[a-zA-Z\s]+$/.test(lastName)) errors.push('Last Name을 영문으로 입력해주세요.');
    if (!gender) errors.push('성별을 선택해주세요.');
    if (!birthYear || !birthMonth || !birthDay) errors.push('생년월일을 입력해주세요.');
    if (!/^\S+@\S+\.\S+$/.test(email)) errors.push('올바른 이메일을 입력해주세요.');
    if (!/^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/.test(phone.replace(/-/g, ''))) errors.push('올바른 휴대폰 번호를 입력해주세요.');
    if (!province) errors.push('시/도를 선택해주세요.');
    if (!district) errors.push('시/군/구를 선택해주세요.');
    if (!allRequiredAgreed) errors.push('필수 약관(서비스 이용약관, 개인정보 처리방침)에 동의해주세요.');
    return errors;
  };

  const handleSubmit = async () => {
    const missing = collectValidationErrors();
    if (missing.length > 0) {
      setValidationErrors(missing);
      return;
    }

    if (!userId) {
      setError('사용자 정보를 찾을 수 없습니다. 다시 로그인해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    const nameEn = cleanAndConcatenateName(firstName, lastName);
    const birthDate = `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`;

    try {
      const response = await fetch('/api/v1/auth/agree-terms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userId,
          termsAgreed: agreements.service,
          privacyAgreed: agreements.privacy,
          marketingAgreed: agreements.marketing,
          name,
          firstName,
          lastName,
          nameEn,
          gender,
          birthDate,
          email,
          phone,
          province,
          district,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || '약관 동의 처리에 실패했습니다.');
      }

      // 방금 저장한 이름 등이 헤더에 바로 반영되도록 서버에서 최신 상태 재조회
      await checkSession();

      // 홈으로 이동
      // 홈 도착 시 환영 모달(회원가입 축하) 표시 — 이메일 인증 완료 흐름과 동일한 모달 재사용
      navigate('/?welcome=true', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex-1 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              환영합니다!
            </h1>
            <p className="text-gray-600">
              서비스 이용을 위해 정보를 입력하고 약관에 동의해주세요.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* 추가 정보 입력 (간편 로그인은 이메일 회원가입과 동일한 필드를 제공하지 않으므로 직접 입력받음 — 인증서/이력서에서 그대로 사용됨) */}
          <div className="space-y-4 mb-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이름 (한글) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="홍길동"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name (이름) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Gildong"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name (성) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Hong"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
              />
              <p className="text-xs text-gray-500 mt-1">예: Gildong Hong (인증서에 표시됩니다)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                성별 <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                    gender === 'male' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value="male"
                    checked={gender === 'male'}
                    onChange={() => setGender('male')}
                    className="sr-only"
                  />
                  <span className="font-medium">남성</span>
                </label>
                <label
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                    gender === 'female' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value="female"
                    checked={gender === 'female'}
                    onChange={() => setGender('female')}
                    className="sr-only"
                  />
                  <span className="font-medium">여성</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                생년월일 <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <select
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none bg-white"
                >
                  <option value="">년도</option>
                  {Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                    <option key={year} value={year}>{year}년</option>
                  ))}
                </select>
                <select
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none bg-white"
                >
                  <option value="">월</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <option key={month} value={month}>{month}월</option>
                  ))}
                </select>
                <select
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value)}
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none bg-white"
                >
                  <option value="">일</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>{day}일</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이메일 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                휴대폰 번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="010-1234-5678"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시/도 <span className="text-red-500">*</span>
              </label>
              <select
                value={province}
                onChange={handleProvinceChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none bg-white"
              >
                <option value="">시/도 선택</option>
                {getAllProvinces().map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시/군/구 <span className="text-red-500">*</span>
              </label>
              <select
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                disabled={!province}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none bg-white ${
                  !province ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-gray-300'
                }`}
              >
                <option value="">시/군/구 선택</option>
                {province && getDistricts(province).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>

          {/* All Agree */}
          <div
            onClick={handleToggleAll}
            className="flex items-center gap-3 p-4 bg-primary/5 rounded-lg cursor-pointer hover:bg-primary/10 transition-colors mb-4"
          >
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                allAgreed
                  ? 'bg-primary border-primary'
                  : 'border-gray-300'
              }`}
            >
              {allAgreed && <Check className="w-4 h-4 text-white" />}
            </div>
            <span className="font-medium text-gray-900">전체 동의</span>
          </div>

          {/* Terms List */}
          <div className="space-y-3 mb-8">
            {terms.map((term) => (
              <div
                key={term.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <div className="flex items-center p-4">
                  <div
                    onClick={() => handleToggle(term.id)}
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        agreements[term.id]
                          ? 'bg-primary border-primary'
                          : 'border-gray-300'
                      }`}
                    >
                      {agreements[term.id] && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-gray-900">
                      {term.required ? (
                        <span className="text-red-500 mr-1">[필수]</span>
                      ) : (
                        <span className="text-gray-400 mr-1">[선택]</span>
                      )}
                      {term.title}
                    </span>
                  </div>
                  <button
                    onClick={() =>
                      setExpandedTerm(expandedTerm === term.id ? null : term.id)
                    }
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <ChevronRight
                      className={`w-5 h-5 transition-transform ${
                        expandedTerm === term.id ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* Expanded Content */}
                {expandedTerm === term.id && (
                  <div className="px-4 pb-4">
                    <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                      <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans">
                        {term.content}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`w-full py-4 rounded-lg font-medium text-white transition-colors ${
              !loading
                ? 'bg-primary hover:bg-primary-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {loading ? '처리 중...' : '동의하고 시작하기'}
          </button>

          <p className="text-center text-sm text-gray-500 mt-4">
            동의하지 않으시면 서비스 이용이 제한됩니다.
          </p>
        </div>
      </div>
      </div>

      {/* 입력 누락 안내 모달 — 스크롤 위치와 무관하게 바로 눈에 띄도록 */}
      {validationErrors.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setValidationErrors([])}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
              <h2 className="text-lg font-bold text-gray-900">입력을 확인해주세요</h2>
            </div>
            <ul className="space-y-2 mb-6">
              {validationErrors.map((msg, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>{msg}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => setValidationErrors([])}
              className="w-full py-3 rounded-lg font-medium text-white bg-primary hover:bg-primary-600 transition-colors"
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
